import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import api from "../../../api/api";

import {
    Table,
    Input,
    Button,
    Modal,
    Form,
    Pagination,
    Popconfirm,
    InputNumber,
    Upload,
    Empty,
    Tooltip,
    Tag,
    Space,
    Select,
} from "antd";

import {
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    UploadOutlined,
    ShoppingCartOutlined,
    ReloadOutlined,
    ClearOutlined,
    FilePdfOutlined,
} from "@ant-design/icons";

import Swal from "sweetalert2";

import {
    pdf,
} from "@react-pdf/renderer";

import ProductListStockDetailsPDF from "./ProductListStockDetailsPDF";


// =====================================================
// CONFIG
// =====================================================

const PAGE_SIZE = 10;
const PDF_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE = 300;
const AUTO_REFRESH_INTERVAL = 30000;


// =====================================================
// PRODUCT IMAGE
// =====================================================

const ProductImage = React.memo(
    ({
        image,
        size = "normal",
    }) => {

        const [
            imageError,
            setImageError,
        ] = useState(false);

        if (
            !image ||
            imageError
        ) {

            return (
                <div
                    className={
                        `product-image-placeholder ${
                            size === "small"
                                ? "product-image-small"
                                : ""
                        }`
                    }
                >
                    <ShoppingCartOutlined />
                </div>
            );
        }

        return (
            <div
                className={
                    `product-image-wrapper ${
                        size === "small"
                            ? "product-image-small"
                            : ""
                    }`
                }
            >
                <img
                    src={image}
                    alt="Product"
                    className="product-image"
                    loading="lazy"
                    decoding="async"
                    onError={() =>
                        setImageError(true)
                    }
                />
            </div>
        );
    }
);


// =====================================================
// PRODUCT LIST
// =====================================================

const ProductList = () => {

    // =================================================
    // DATA
    // =================================================

    const [
        products,
        setProducts,
    ] = useState([]);

    const [
        totalProducts,
        setTotalProducts,
    ] = useState(0);


    // =================================================
    // UI
    // =================================================

    const [
        loading,
        setLoading,
    ] = useState(false);

    const [
        refreshing,
        setRefreshing,
    ] = useState(false);

    const [
        pdfLoading,
        setPdfLoading,
    ] = useState(false);

    const [
        searchText,
        setSearchText,
    ] = useState("");

    const [
        currentPage,
        setCurrentPage,
    ] = useState(1);


    // =================================================
    // EDIT
    // =================================================

    const [
        isModalOpen,
        setIsModalOpen,
    ] = useState(false);

    const [
        editingProduct,
        setEditingProduct,
    ] = useState(null);

    const [
        selectedImage,
        setSelectedImage,
    ] = useState(null);

    const [
        previewImage,
        setPreviewImage,
    ] = useState("");


    // =================================================
    // FORM
    // =================================================

    const [
        form,
    ] = Form.useForm();

    const selectedUnitType =
        Form.useWatch(
            "unit_type",
            form
        );


    // =================================================
    // REFS
    // =================================================

    const abortControllerRef =
        useRef(null);

    const requestIdRef =
        useRef(0);

    const searchTimeoutRef =
        useRef(null);

    const pageRef =
        useRef(1);

    const searchRef =
        useRef("");


    // =================================================
    // NUMBER FORMAT
    // =================================================

    const formatNumber =
        useCallback(
            (
                value,
                decimals = 2
            ) => {

                return Number(
                    value || 0
                ).toLocaleString(
                    undefined,
                    {
                        minimumFractionDigits:
                            decimals,

                        maximumFractionDigits:
                            decimals,
                    }
                );
            },
            []
        );


    // =================================================
    // MONEY
    // =================================================

    const formatMoney =
        useCallback(
            (
                value
            ) => {

                return `L$ ${formatNumber(
                    value,
                    2
                )}`;
            },
            [
                formatNumber,
            ]
        );


    // =================================================
    // CANCEL REQUEST
    // =================================================

    const cancelCurrentRequest =
        useCallback(
            () => {

                if (
                    abortControllerRef.current
                ) {

                    abortControllerRef.current.abort();

                    abortControllerRef.current =
                        null;
                }
            },
            []
        );


    // =================================================
    // FETCH PRODUCTS
    // =================================================

    const fetchProducts =
        useCallback(
            async ({
                page = 1,
                search = "",
                background = false,
            } = {}) => {

                cancelCurrentRequest();

                const controller =
                    new AbortController();

                abortControllerRef.current =
                    controller;

                const requestId =
                    ++requestIdRef.current;

                const cleanSearch =
                    String(
                        search || ""
                    ).trim();

                try {

                    if (
                        background
                    ) {

                        setRefreshing(
                            true
                        );

                    } else {

                        setLoading(
                            true
                        );
                    }

                    const params =
                        new URLSearchParams();

                    params.set(
                        "page",
                        String(page)
                    );

                    params.set(
                        "page_size",
                        String(PAGE_SIZE)
                    );

                    if (
                        cleanSearch
                    ) {

                        params.set(
                            "search",
                            cleanSearch
                        );
                    }

                    const response =
                        await api.get(
                            `/api/products/?${params.toString()}`,
                            {
                                signal:
                                    controller.signal,
                            }
                        );

                    if (
                        requestId !==
                        requestIdRef.current
                    ) {

                        return;
                    }

                    const results =
                        Array.isArray(
                            response.data?.results
                        )
                            ? response.data.results
                            : [];

                    const count =
                        Number(
                            response.data?.count ||
                            0
                        );

                    setProducts(
                        results
                    );

                    setTotalProducts(
                        count
                    );

                    pageRef.current =
                        page;

                    searchRef.current =
                        cleanSearch;

                    setCurrentPage(
                        page
                    );

                } catch (
                    error
                ) {

                    if (
                        error?.name ===
                            "CanceledError" ||
                        error?.name ===
                            "AbortError" ||
                        error?.code ===
                            "ERR_CANCELED"
                    ) {

                        return;
                    }

                    console.error(
                        "PRODUCT FETCH ERROR:",
                        error
                    );

                    Swal.fire({
                        icon:
                            "error",

                        title:
                            "Unable to load products",

                        text:
                            error?.response?.data?.detail ||
                            "Something went wrong while loading products.",
                    });

                } finally {

                    if (
                        requestId ===
                        requestIdRef.current
                    ) {

                        setLoading(
                            false
                        );

                        setRefreshing(
                            false
                        );
                    }
                }
            },
            [
                cancelCurrentRequest,
            ]
        );


    // =================================================
    // INITIAL LOAD
    // =================================================

    useEffect(
        () => {

            fetchProducts({
                page: 1,
                search: "",
            });

            return () => {

                cancelCurrentRequest();

                if (
                    searchTimeoutRef.current
                ) {

                    clearTimeout(
                        searchTimeoutRef.current
                    );
                }
            };

        },
        [
            fetchProducts,
            cancelCurrentRequest,
        ]
    );


    // =================================================
    // AUTO REFRESH
    // =================================================

    useEffect(
        () => {

            const interval =
                setInterval(
                    () => {

                        fetchProducts({
                            page:
                                pageRef.current,

                            search:
                                searchRef.current,

                            background:
                                true,
                        });

                    },
                    AUTO_REFRESH_INTERVAL
                );

            return () => {

                clearInterval(
                    interval
                );
            };

        },
        [
            fetchProducts,
        ]
    );


    // =================================================
    // SEARCH
    // =================================================

    const handleSearch =
        useCallback(
            (
                value
            ) => {

                setSearchText(
                    value
                );

                searchRef.current =
                    value.trim();

                pageRef.current =
                    1;

                setCurrentPage(
                    1
                );

                if (
                    searchTimeoutRef.current
                ) {

                    clearTimeout(
                        searchTimeoutRef.current
                    );
                }

                cancelCurrentRequest();

                if (
                    !value.trim()
                ) {

                    fetchProducts({
                        page: 1,
                        search: "",
                    });

                    return;
                }

                searchTimeoutRef.current =
                    setTimeout(
                        () => {

                            fetchProducts({
                                page: 1,
                                search: value,
                            });

                        },
                        SEARCH_DEBOUNCE
                    );

            },
            [
                fetchProducts,
                cancelCurrentRequest,
            ]
        );


    // =================================================
    // CLEAR SEARCH
    // =================================================

    const handleClearSearch =
        useCallback(
            () => {

                if (
                    searchTimeoutRef.current
                ) {

                    clearTimeout(
                        searchTimeoutRef.current
                    );
                }

                cancelCurrentRequest();

                setSearchText(
                    ""
                );

                searchRef.current =
                    "";

                pageRef.current =
                    1;

                setCurrentPage(
                    1
                );

                fetchProducts({
                    page: 1,
                    search: "",
                });

            },
            [
                fetchProducts,
                cancelCurrentRequest,
            ]
        );


    // =================================================
    // REFRESH
    // =================================================

    const handleRefresh =
        useCallback(
            () => {

                fetchProducts({
                    page:
                        pageRef.current,

                    search:
                        searchRef.current,

                    background:
                        true,
                });

            },
            [
                fetchProducts,
            ]
        );


    // =================================================
    // PAGE
    // =================================================

    const handlePageChange =
        useCallback(
            (
                page
            ) => {

                pageRef.current =
                    page;

                setCurrentPage(
                    page
                );

                fetchProducts({
                    page,

                    search:
                        searchRef.current,
                });

            },
            [
                fetchProducts,
            ]
        );


    // =================================================
    // DELETE
    // =================================================

    const handleDelete =
        useCallback(
            async (
                id
            ) => {

                try {

                    setLoading(
                        true
                    );

                    await api.delete(
                        `/api/products/${id}/`
                    );

                    await Swal.fire({
                        icon:
                            "success",

                        title:
                            "Product Deleted",

                        text:
                            "The product was deleted successfully.",

                        timer:
                            1600,

                        showConfirmButton:
                            false,
                    });

                    let nextPage =
                        pageRef.current;

                    if (
                        products.length === 1 &&
                        nextPage > 1
                    ) {

                        nextPage -= 1;
                    }

                    pageRef.current =
                        nextPage;

                    setCurrentPage(
                        nextPage
                    );

                    await fetchProducts({
                        page:
                            nextPage,

                        search:
                            searchRef.current,
                    });

                } catch (
                    error
                ) {

                    console.error(
                        "DELETE PRODUCT ERROR:",
                        error
                    );

                    Swal.fire({
                        icon:
                            "error",

                        title:
                            "Delete Failed",

                        text:
                            error?.response?.data?.detail ||
                            "Failed to delete product.",
                    });

                } finally {

                    setLoading(
                        false
                    );
                }

            },
            [
                products.length,
                fetchProducts,
            ]
        );


    // =================================================
    // EDIT
    // =================================================

    const handleEdit =
        useCallback(
            (
                product
            ) => {

                setEditingProduct(
                    product
                );

                setPreviewImage(
                    product.image || ""
                );

                setSelectedImage(
                    null
                );

                form.setFieldsValue({

                    name:
                        product.name,

                    unit_type:
                        product.unit_type,

                    quantity:
                        product.quantity,

                    units_per_carton:
                        product.units_per_carton,

                    price:
                        product.price,

                    wholesale_price:
                        product.wholesale_price,

                });

                setIsModalOpen(
                    true
                );

            },
            [
                form,
            ]
        );


    // =================================================
    // IMAGE CHANGE
    // =================================================

    const handleImageChange =
        useCallback(
            (
                info
            ) => {

                const file =
                    info.fileList?.[0]
                        ?.originFileObj;

                if (!file) {

                    return;
                }

                setSelectedImage(
                    file
                );

                const objectUrl =
                    URL.createObjectURL(
                        file
                    );

                setPreviewImage(
                    objectUrl
                );

            },
            []
        );


    // =================================================
    // CLOSE MODAL
    // =================================================

    const closeModal =
        useCallback(
            () => {

                setIsModalOpen(
                    false
                );

                setEditingProduct(
                    null
                );

                setSelectedImage(
                    null
                );

                setPreviewImage(
                    ""
                );

                form.resetFields();

            },
            [
                form,
            ]
        );


    // =================================================
    // UPDATE
    // =================================================

    const handleUpdate =
        useCallback(
            async (
                values
            ) => {

                if (
                    !editingProduct
                ) {

                    return;
                }

                try {

                    setLoading(
                        true
                    );

                    const formData =
                        new FormData();

                    formData.append(
                        "name",
                        String(
                            values.name || ""
                        ).trim()
                    );

                    formData.append(
                        "unit_type",
                        values.unit_type
                    );

                    formData.append(
                        "quantity",
                        String(
                            values.quantity ?? 0
                        )
                    );

                    formData.append(
                        "units_per_carton",
                        String(
                            values.unit_type ===
                            "carton"
                                ? values.units_per_carton ?? 1
                                : 1
                        )
                    );

                    formData.append(
                        "price",
                        String(
                            values.price ?? 0
                        )
                    );

                    formData.append(
                        "wholesale_price",
                        String(
                            values.wholesale_price ?? 0
                        )
                    );

                    if (
                        selectedImage
                    ) {

                        formData.append(
                            "image",
                            selectedImage
                        );
                    }

                    await api.patch(
                        `/api/products/${editingProduct.id}/`,
                        formData,
                        {
                            headers: {
                                "Content-Type":
                                    "multipart/form-data",
                            },
                        }
                    );

                    await Swal.fire({
                        icon:
                            "success",

                        title:
                            "Product Updated",

                        text:
                            "Product information updated successfully.",

                        timer:
                            1600,

                        showConfirmButton:
                            false,
                    });

                    closeModal();

                    await fetchProducts({
                        page:
                            pageRef.current,

                        search:
                            searchRef.current,
                    });

                } catch (
                    error
                ) {

                    console.error(
                        "UPDATE PRODUCT ERROR:",
                        error
                    );

                    const data =
                        error?.response?.data;

                    let message =
                        "Failed to update product.";

                    if (
                        typeof data ===
                        "object"
                    ) {

                        const firstError =
                            Object.values(
                                data
                            )?.[0];

                        if (
                            Array.isArray(
                                firstError
                            )
                        ) {

                            message =
                                firstError[0];

                        } else if (
                            firstError
                        ) {

                            message =
                                String(
                                    firstError
                                );
                        }
                    }

                    Swal.fire({
                        icon:
                            "error",

                        title:
                            "Update Failed",

                        text:
                            message,
                    });

                } finally {

                    setLoading(
                        false
                    );
                }

            },
            [
                editingProduct,
                selectedImage,
                closeModal,
                fetchProducts,
            ]
        );


    // =================================================
    // FETCH ALL PRODUCTS FOR PDF
    // =================================================

    const fetchAllProductsForPDF =
        useCallback(
            async () => {

                const allProducts = [];

                let page = 1;

                let hasMore = true;

                while (
                    hasMore
                ) {

                    const params =
                        new URLSearchParams();

                    params.set(
                        "page",
                        String(page)
                    );

                    params.set(
                        "page_size",
                        String(PDF_PAGE_SIZE)
                    );

                    if (
                        searchRef.current
                    ) {

                        params.set(
                            "search",
                            searchRef.current
                        );
                    }

                    const response =
                        await api.get(
                            `/api/products/?${params.toString()}`
                        );

                    const results =
                        Array.isArray(
                            response.data?.results
                        )
                            ? response.data.results
                            : [];

                    allProducts.push(
                        ...results
                    );

                    hasMore =
                        Boolean(
                            response.data?.next
                        ) &&
                        results.length > 0;

                    page += 1;

                    if (
                        page > 1000
                    ) {

                        break;
                    }
                }

                return allProducts;

            },
            []
        );


    // =================================================
    // PDF
    // =================================================

    const handleGeneratePDF =
        useCallback(
            async () => {

                try {

                    setPdfLoading(
                        true
                    );

                    const allProducts =
                        await fetchAllProductsForPDF();

                    if (
                        !allProducts.length
                    ) {

                        Swal.fire({
                            icon:
                                "info",

                            title:
                                "No Products",

                            text:
                                "There are no products available.",
                        });

                        return;
                    }

                    const blob =
                        await pdf(
                            <ProductListStockDetailsPDF
                                products={
                                    allProducts
                                }
                            />
                        ).toBlob();

                    const url =
                        URL.createObjectURL(
                            blob
                        );

                    const link =
                        document.createElement(
                            "a"
                        );

                    link.href =
                        url;

                    link.download =
                        `Zuluwhiskey-Stock-Listing-${new Date()
                            .toISOString()
                            .slice(0, 10)}.pdf`;

                    document.body.appendChild(
                        link
                    );

                    link.click();

                    link.remove();

                    setTimeout(
                        () => {

                            URL.revokeObjectURL(
                                url
                            );

                        },
                        1000
                    );

                } catch (
                    error
                ) {

                    console.error(
                        "PDF GENERATION ERROR:",
                        error
                    );

                    Swal.fire({
                        icon:
                            "error",

                        title:
                            "PDF Generation Failed",

                        text:
                            error?.message ||
                            "Unable to generate the stock PDF.",
                    });

                } finally {

                    setPdfLoading(
                        false
                    );
                }

            },
            [
                fetchAllProductsForPDF,
            ]
        );


    // =================================================
    // TABLE COLUMNS
    // =================================================

    const columns =
        useMemo(
            () => [

                {
                    title:
                        "Image",

                    dataIndex:
                        "image",

                    key:
                        "image",

                    width:
                        70,

                    align:
                        "center",

                    render:
                        image => (
                            <ProductImage
                                image={
                                    image
                                }
                                size="small"
                            />
                        ),
                },

                {
                    title:
                        "Product",

                    dataIndex:
                        "name",

                    key:
                        "name",

                    width:
                        150,

                    ellipsis:
                        true,

                    render:
                        name => (
                            <Tooltip
                                title={
                                    name
                                }
                            >
                                <div
                                    className="product-name-cell"
                                >
                                    <strong>
                                        {
                                            name ||
                                            "Unnamed Product"
                                        }
                                    </strong>
                                </div>
                            </Tooltip>
                        ),
                },

                {
                    title:
                        "Type",

                    dataIndex:
                        "unit_type",

                    key:
                        "unit_type",

                    width:
                        80,

                    align:
                        "center",

                    render:
                        value => (
                            <Tag
                                color={
                                    value ===
                                    "carton"
                                        ? "blue"
                                        : "default"
                                }
                            >
                                {
                                    value ===
                                    "carton"
                                        ? "Carton"
                                        : "Single"
                                }
                            </Tag>
                        ),
                },

                {
                    title:
                        "Qty",

                    dataIndex:
                        "quantity",

                    key:
                        "quantity",

                    width:
                        75,

                    align:
                        "center",

                    render:
                        (
                            value,
                            record
                        ) => (
                            <strong>
                                {
                                    formatNumber(
                                        value,
                                        0
                                    )
                                }{" "}
                                {
                                    record.unit_type ===
                                    "carton"
                                        ? "ctn"
                                        : "item"
                                }
                            </strong>
                        ),
                },

               

                {
                    title:
                        "Total Pcs",

                    dataIndex:
                        "units_per_carton",

                    key:
                        "units_per_carton",

                    width:
                        85,

                    align:
                        "center",

                    render:
                        value => (
                            <strong>
                                {
                                    formatNumber(
                                        value,
                                        0
                                    )
                                }
                            </strong>
                        ),
                },

                {
                    title:
                        "Sell/Pc",

                    dataIndex:
                        "price",

                    key:
                        "price",

                    width:
                        95,

                    align:
                        "right",

                    render:
                        value =>
                            formatMoney(
                                value
                            ),
                },

                {
                    title:
                        "Cost/Ctn",

                    dataIndex:
                        "wholesale_price",

                    key:
                        "wholesale_price",

                    width:
                        105,

                    align:
                        "right",

                    render:
                        value =>
                            formatMoney(
                                value
                            ),
                },

                {
                    title:
                        "Value/Ctn",

                    dataIndex:
                        "selling_value_per_carton",

                    key:
                        "selling_value_per_carton",

                    width:
                        110,

                    align:
                        "right",

                    render:
                        value => (
                            <strong>
                                {
                                    formatMoney(
                                        value
                                    )
                                }
                            </strong>
                        ),
                },



                {
                    title:
                        "Stock Value",

                    dataIndex:
                        "total_stock_selling_value",

                    key:
                        "total_stock_selling_value",

                    width:
                        120,

                    align:
                        "right",

                    render:
                        value => (
                            <strong>
                                {
                                    formatMoney(
                                        value
                                    )
                                }
                            </strong>
                        ),
                },

                                {
                    title:
                        "Profit/Ctn",

                    dataIndex:
                        "profit_per_carton",

                    key:
                        "profit_per_carton",

                    width:
                        105,

                    align:
                        "right",

                    render:
                        value => (
                            <strong
                                className="profit-value"
                            >
                                {
                                    formatMoney(
                                        value
                                    )
                                }
                            </strong>
                        ),
                },
                
                {
                    title:
                        "Created By",

                    dataIndex:
                        "created_by",

                    key:
                        "created_by",

                    width:
                        115,

                    ellipsis:
                        true,

                    render:
                        value =>
                            value?.username ||
                            "—",
                },

                {
                    title:
                        "Created",

                    dataIndex:
                        "created_at",

                    key:
                        "created_at",

                    width:
                        100,

                    render:
                        date =>
                            date
                                ? new Date(
                                    date
                                ).toLocaleDateString()
                                : "—",
                },

                {
                    title:
                        "Actions",

                    key:
                        "actions",

                    width:
                        95,

                    fixed:
                        "right",

                    align:
                        "center",

                    render:
                        (
                            _,
                            record
                        ) => (
                            <Space
                                size={4}
                            >

                                <Tooltip
                                    title="Edit Product"
                                >
                                    <Button
                                        size="small"
                                        type="primary"
                                        icon={
                                            <EditOutlined />
                                        }
                                        onClick={() =>
                                            handleEdit(
                                                record
                                            )
                                        }
                                    />
                                </Tooltip>

                                <Popconfirm
                                    title="Delete Product"
                                    description={
                                        `Delete "${record.name}"?`
                                    }
                                    onConfirm={() =>
                                        handleDelete(
                                            record.id
                                        )
                                    }
                                    okText="Delete"
                                    cancelText="Cancel"
                                    placement="topRight"
                                >

                                    <Tooltip
                                        title="Delete Product"
                                    >
                                        <Button
                                            size="small"
                                            danger
                                            icon={
                                                <DeleteOutlined />
                                            }
                                        />
                                    </Tooltip>

                                </Popconfirm>

                            </Space>
                        ),
                },

            ],
            [
                formatMoney,
                formatNumber,
                handleDelete,
                handleEdit,
            ]
        );


    // =================================================
    // MOBILE CARD
    // =================================================

    const renderMobileProduct =
        useCallback(
            product => (

                <div
                    className="mobile-product-card"
                    key={
                        product.id
                    }
                >

                    <div
                        className="mobile-product-header"
                    >

                        <ProductImage
                            image={
                                product.image
                            }
                        />

                        <div
                            className="mobile-product-title"
                        >

                            <strong>
                                {
                                    product.name ||
                                    "Unnamed Product"
                                }
                            </strong>

                            <span>
                                Product ID:{" "}
                                {
                                    product.id
                                }
                            </span>

                        </div>

                        <Tag
                            color={
                                product.unit_type ===
                                "carton"
                                    ? "blue"
                                    : "default"
                            }
                        >
                            {
                                product.unit_type ===
                                "carton"
                                    ? "Carton"
                                    : "Single"
                            }
                        </Tag>

                    </div>

                    <div
                        className="mobile-product-grid"
                    >

                        <div>
                            <span>
                                Quantity
                            </span>

                            <strong>
                                {
                                    formatNumber(
                                        product.quantity,
                                        0
                                    )
                                }{" "}
                                {
                                    product.unit_type ===
                                    "carton"
                                        ? "carton(s)"
                                        : "item(s)"
                                }
                            </strong>
                        </div>

                        <div>
                            <span>
                                Pieces / Carton
                            </span>

                            <strong>
                                {
                                    product.unit_type ===
                                    "carton"
                                        ? formatNumber(
                                            product.units_per_carton,
                                            0
                                        )
                                        : "—"
                                }
                            </strong>
                        </div>

                        <div>
                            <span>
                                Total Pieces
                            </span>

                            <strong>
                                {
                                    formatNumber(
                                        product.total_units,
                                        0
                                    )
                                }
                            </strong>
                        </div>

                        <div>
                            <span>
                                Selling / Piece
                            </span>

                            <strong>
                                {
                                    formatMoney(
                                        product.price
                                    )
                                }
                            </strong>
                        </div>

                        <div>
                            <span>
                                Cost / Carton
                            </span>

                            <strong>
                                {
                                    formatMoney(
                                        product.wholesale_price
                                    )
                                }
                            </strong>
                        </div>

                        <div>
                            <span>
                                Value / Carton
                            </span>

                            <strong>
                                {
                                    formatMoney(
                                        product.selling_value_per_carton
                                    )
                                }
                            </strong>
                        </div>

                        <div>
                            <span>
                                Profit / Carton
                            </span>

                            <strong
                                className="profit-value"
                            >
                                {
                                    formatMoney(
                                        product.profit_per_carton
                                    )
                                }
                            </strong>
                        </div>

                        <div>
                            <span>
                                Total Stock Value
                            </span>

                            <strong>
                                {
                                    formatMoney(
                                        product.total_stock_selling_value
                                    )
                                }
                            </strong>
                        </div>

                        <div
                            className="mobile-profit-box"
                        >

                            <span>
                                Total Profit
                            </span>

                            <strong>
                                {
                                    formatMoney(
                                        product.total_profit
                                    )
                                }
                            </strong>

                        </div>

                        <div>
                            <span>
                                Created By
                            </span>

                            <strong>
                                {
                                    product.created_by
                                        ?.username ||
                                    "—"
                                }
                            </strong>
                        </div>

                        <div>
                            <span>
                                Created
                            </span>

                            <strong>
                                {
                                    product.created_at
                                        ? new Date(
                                            product.created_at
                                        ).toLocaleDateString()
                                        : "—"
                                }
                            </strong>
                        </div>

                    </div>

                    <div
                        className="mobile-product-actions"
                    >

                        <Button
                            type="primary"
                            icon={
                                <EditOutlined />
                            }
                            onClick={() =>
                                handleEdit(
                                    product
                                )
                            }
                        >
                            Edit
                        </Button>

                        <Popconfirm
                            title="Delete Product"
                            description={
                                `Delete "${product.name}"?`
                            }
                            onConfirm={() =>
                                handleDelete(
                                    product.id
                                )
                            }
                            okText="Delete"
                            cancelText="Cancel"
                        >

                            <Button
                                danger
                                icon={
                                    <DeleteOutlined />
                                }
                            >
                                Delete
                            </Button>

                        </Popconfirm>

                    </div>

                </div>

            ),
            [
                formatMoney,
                formatNumber,
                handleDelete,
                handleEdit,
            ]
        );


    // =================================================
    // RENDER
    // =================================================

    return (

        <>

            <style>
                {`

                /* =====================================================
                   GLOBAL
                ===================================================== */

                html,
                body {
                    max-width: 100%;
                    overflow-x: hidden;
                }

                #root {
                    max-width: 100%;
                    overflow-x: hidden;
                }


                /* =====================================================
                   PRODUCT CONTAINER
                ===================================================== */

                .product-list-container {
                    width: 100%;
                    min-height: 100vh;
                    padding: 20px;
                    background: #f1f5f9;
                    box-sizing: border-box;
                    overflow-x: hidden;
                }


                /* =====================================================
                   HEADER
                ===================================================== */

                .product-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 15px;
                    margin-bottom: 20px;
                    flex-wrap: wrap;
                }

                .product-header-title {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                }

                .product-header h2 {
                    margin: 0;
                    color: #0f172a;
                    font-size: 28px;
                    font-weight: 700;
                    line-height: 1.2;
                }

                .product-header h2 + p {
                    margin: 4px 0 0;
                    color: #64748b;
                    font-size: 13px;
                }

                .product-count {
                    color: #64748b;
                    font-size: 14px;
                    font-weight: 500;
                }


                /* =====================================================
                   HEADER ACTIONS
                ===================================================== */

                .product-header-actions {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 8px;
                    flex-wrap: wrap;
                }

                .product-search-input {
                    width: 300px;
                    border-radius: 10px;
                }

                .product-search-input .ant-input {
                    border-radius: 10px;
                }


                /* =====================================================
                   SEARCH STATUS
                ===================================================== */

                .search-status {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 5px;
                    margin-bottom: 15px;
                    padding: 10px 14px;
                    background: #ffffff;
                    color: #64748b;
                    font-size: 14px;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    box-sizing: border-box;
                }

                .search-status strong {
                    color: #0f172a;
                }

                .search-status span {
                    color: #94a3b8;
                }


                /* =====================================================
                   DESKTOP TABLE
                ===================================================== */

                .product-desktop-table {
                    width: 100%;
                    max-width: 100%;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 14px;
                    overflow: hidden;
                    box-shadow:
                        0 4px 18px rgba(15, 23, 42, 0.05);
                    box-sizing: border-box;
                }

                .product-desktop-table .ant-table-wrapper,
                .product-desktop-table .ant-table,
                .product-desktop-table .ant-table-container {
                    width: 100%;
                    max-width: 100%;
                    background: #ffffff;
                }


                /* =====================================================
                   TABLE HEADER
                ===================================================== */

                .product-desktop-table
                .ant-table-thead > tr > th {
                    height: 48px;
                    padding: 10px 8px !important;
                    background: #0f172a !important;
                    color: #ffffff !important;
                    font-size: 12px;
                    font-weight: 700;
                    white-space: nowrap;
                    border-bottom: 1px solid #1e293b !important;
                }

                .product-desktop-table
                .ant-table-thead > tr > th::before {
                    display: none !important;
                }


                /* =====================================================
                   TABLE BODY
                ===================================================== */

                .product-desktop-table
                .ant-table-tbody > tr {
                    position: relative;
                    transition:
                        transform 0.2s ease,
                        box-shadow 0.25s ease;
                }

                .product-desktop-table
                .ant-table-tbody > tr > td {
                    height: 58px;
                    padding: 7px 8px !important;
                    color: #334155;
                    font-size: 13px;
                    background: #ffffff;
                    border-bottom: 1px solid #f1f5f9;
                    vertical-align: middle;
                    box-sizing: border-box;
                    transition:
                        background-color 0.25s ease,
                        color 0.25s ease,
                        border-color 0.25s ease;
                }


                /* =====================================================
                   ORANGE ROW HOVER
                ===================================================== */

                .product-desktop-table
                .ant-table-tbody
                > tr:hover
                > td {
                    background: #fff7ed !important;
                    border-top-color: #fed7aa !important;
                    border-bottom-color: #fdba74 !important;
                }

                .product-desktop-table
                .ant-table-tbody
                > tr:hover
                > td:first-child {
                    border-left: 3px solid #f97316 !important;
                    padding-left: 5px !important;
                }

                .product-desktop-table
                .ant-table-tbody
                > tr:hover {
                    transform: translateY(-1px);
                    box-shadow:
                        0 3px 12px rgba(249, 115, 22, 0.08);
                }

                .product-name-cell {
                    overflow: hidden;
                    color: #0f172a;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                }

                .product-name-cell strong {
                    font-size: 13px;
                    font-weight: 600;
                }

                .product-desktop-table
                .ant-table-tbody
                > tr:hover
                .product-name-cell {
                    color: #ea580c;
                }


                /* =====================================================
                   PRODUCT IMAGE
                ===================================================== */

                .product-image-wrapper {
                    width: 46px;
                    height: 46px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                    overflow: hidden;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 9px;
                    box-sizing: border-box;
                    transition:
                        border-color 0.25s ease,
                        transform 0.25s ease;
                }

                .product-image-wrapper.product-image-small {
                    width: 40px;
                    height: 40px;
                }

                .product-image {
                    display: block;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    border-radius: 8px;
                }

                .product-image-placeholder {
                    width: 46px;
                    height: 46px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 9px;
                    color: #94a3b8;
                    font-size: 22px;
                    box-sizing: border-box;
                    transition:
                        border-color 0.25s ease,
                        color 0.25s ease;
                }

                .product-image-placeholder.product-image-small {
                    width: 40px;
                    height: 40px;
                    font-size: 18px;
                }

                .product-desktop-table
                .ant-table-tbody
                > tr:hover
                .product-image-wrapper,
                .product-desktop-table
                .ant-table-tbody
                > tr:hover
                .product-image-placeholder {
                    border-color: #fdba74;
                    transform: scale(1.03);
                }


                /* =====================================================
                   FINANCIAL VALUES
                ===================================================== */

                .profit-value {
                    color: #16a34a;
                    font-weight: 700;
                    white-space: nowrap;
                }


                /* =====================================================
                   TABLE TAGS
                ===================================================== */

                .product-desktop-table .ant-tag {
                    margin: 0;
                    padding: 2px 7px;
                    border-radius: 6px;
                    font-size: 11px;
                    font-weight: 600;
                }


                /* =====================================================
                   TABLE BUTTONS
                ===================================================== */

                .product-desktop-table .ant-btn {
                    border-radius: 7px;
                    transition:
                        transform 0.2s ease,
                        box-shadow 0.2s ease;
                }

                .product-desktop-table .ant-btn:hover {
                    transform: translateY(-1px);
                }


                /* =====================================================
                   EMPTY STATE
                ===================================================== */

                .product-desktop-table .ant-empty {
                    padding: 35px 10px;
                }

                .product-desktop-table .ant-empty-description {
                    color: #64748b;
                }


                /* =====================================================
                   MOBILE LIST
                ===================================================== */

                .product-mobile-list {
                    display: none;
                    width: 100%;
                    box-sizing: border-box;
                }


                /* =====================================================
                   MOBILE CARD
                ===================================================== */

                .mobile-product-card {
                    width: 100%;
                    margin-bottom: 12px;
                    padding: 14px;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 14px;
                    box-shadow:
                        0 3px 12px rgba(15, 23, 42, 0.05);
                    box-sizing: border-box;
                    transition:
                        border-color 0.25s ease,
                        box-shadow 0.25s ease,
                        transform 0.2s ease;
                }

                .mobile-product-card:hover {
                    border-color: #fdba74;
                    box-shadow:
                        0 5px 16px rgba(249, 115, 22, 0.08);
                    transform: translateY(-1px);
                }


                /* =====================================================
                   MOBILE HEADER
                ===================================================== */

                .mobile-product-header {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    width: 100%;
                    padding-bottom: 12px;
                    border-bottom: 1px solid #f1f5f9;
                }

                .mobile-product-title {
                    min-width: 0;
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }

                .mobile-product-title strong {
                    overflow: hidden;
                    color: #0f172a;
                    font-size: 15px;
                    font-weight: 700;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                }

                .mobile-product-title span {
                    color: #94a3b8;
                    font-size: 11px;
                }


                /* =====================================================
                   MOBILE GRID
                ===================================================== */

                .mobile-product-grid {
                    display: grid;
                    grid-template-columns:
                        repeat(2, minmax(0, 1fr));
                    gap: 8px;
                    padding: 12px 0;
                }

                .mobile-product-grid > div {
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                    padding: 8px;
                    background: #f8fafc;
                    border: 1px solid #f1f5f9;
                    border-radius: 8px;
                    box-sizing: border-box;
                }

                .mobile-product-grid span {
                    color: #64748b;
                    font-size: 10px;
                    font-weight: 500;
                }

                .mobile-product-grid strong {
                    overflow: hidden;
                    color: #0f172a;
                    font-size: 12px;
                    font-weight: 700;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                }

                .mobile-product-grid
                .mobile-profit-box {
                    background: #f0fdf4;
                    border-color: #bbf7d0;
                }

                .mobile-profit-box strong {
                    color: #16a34a;
                }


                /* =====================================================
                   MOBILE ACTIONS
                ===================================================== */

                .mobile-product-actions {
                    display: grid;
                    grid-template-columns:
                        1fr 1fr;
                    gap: 8px;
                    padding-top: 10px;
                    border-top: 1px solid #f1f5f9;
                }

                .mobile-product-actions .ant-btn {
                    height: 38px;
                    border-radius: 8px;
                    font-weight: 600;
                }


                /* =====================================================
                   MOBILE LOADING
                ===================================================== */

                .mobile-loading {
                    width: 100%;
                    padding: 35px 15px;
                    text-align: center;
                    color: #64748b;
                    background: #ffffff;
                    border-radius: 12px;
                    box-sizing: border-box;
                }


                /* =====================================================
                   PAGINATION
                ===================================================== */

                .pagination-container {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    margin-top: 18px;
                    padding: 5px 0 20px;
                    box-sizing: border-box;
                }

                .pagination-container .ant-pagination {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    justify-content: center;
                }


                /* =====================================================
                   COMPACT EDIT MODAL
                ===================================================== */

                .product-edit-modal .ant-modal {
                    max-width: calc(100vw - 30px);
                    margin: 15px auto;
                }

                .product-edit-modal .ant-modal-content {
                    padding: 0 !important;
                    overflow: hidden;
                    background: #ffffff;
                    border-radius: 18px;
                    box-shadow:
                        0 20px 60px rgba(15, 23, 42, 0.20);
                }

                .product-edit-modal .ant-modal-header {
                    margin-bottom: 0 !important;
                    padding: 15px 20px !important;
                    background: #ffffff;
                    border-bottom: 1px solid #e2e8f0;
                }

                .edit-product-modal-title {
                    display: flex;
                    align-items: center;
                    justify-content: flex-start;
                    gap: 9px;
                    color: #0f172a;
                    font-size: 17px;
                    font-weight: 700;
                }

                .edit-product-modal-title
                .anticon {
                    color: #f97316;
                }


                /* =====================================================
                   COMPACT MODAL BODY
                ===================================================== */

                .product-edit-modal .ant-modal-body {
                    max-height: calc(100vh - 120px);
                    padding: 16px 20px 18px !important;
                    overflow-y: auto;
                    overflow-x: hidden;
                    box-sizing: border-box;
                }

                .product-edit-modal .ant-modal-body::-webkit-scrollbar {
                    width: 5px;
                }

                .product-edit-modal .ant-modal-body::-webkit-scrollbar-track {
                    background: transparent;
                }

                .product-edit-modal .ant-modal-body::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 10px;
                }

                .product-edit-modal .ant-modal-body::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }


                /* =====================================================
                   MODAL FORM LAYOUT
                ===================================================== */

                .edit-product-modal {
                    width: 100%;
                    box-sizing: border-box;
                }

                .edit-product-form {
                    width: 100%;
                }


                /* =====================================================
                   IMAGE + UPLOAD COMPACT ROW
                ===================================================== */

                .edit-product-image-row {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    width: 100%;
                    margin-bottom: 14px;
                    padding: 10px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    box-sizing: border-box;
                }

                .edit-product-image-wrapper {
                    flex: 0 0 82px;
                    width: 82px;
                    height: 82px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                    background: #ffffff;
                    border: 1px solid #dbe3ec;
                    border-radius: 10px;
                    box-sizing: border-box;
                }

                .edit-product-image {
                    display: block;
                    width: 100%;
                    height: 100%;
                    padding: 4px;
                    object-fit: contain;
                    box-sizing: border-box;
                }

                .edit-product-image-placeholder {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #94a3b8;
                    font-size: 30px;
                }

                .edit-product-image-actions {
                    flex: 1;
                    min-width: 0;
                }

                .edit-product-image-actions-title {
                    margin-bottom: 3px;
                    color: #0f172a;
                    font-size: 13px;
                    font-weight: 700;
                }

                .edit-product-image-actions-text {
                    margin-bottom: 8px;
                    color: #94a3b8;
                    font-size: 11px;
                    line-height: 1.4;
                }

                .edit-product-image-actions .ant-upload {
                    display: block;
                }

                .edit-product-image-actions .ant-btn {
                    height: 34px;
                    border-radius: 7px;
                    font-size: 12px;
                    font-weight: 600;
                }


                /* =====================================================
                   FORM GRID
                ===================================================== */

                .edit-form-grid {
                    display: grid;
                    grid-template-columns:
                        repeat(2, minmax(0, 1fr));
                    column-gap: 12px;
                    row-gap: 0;
                    width: 100%;
                }

                .edit-form-full {
                    grid-column: 1 / -1;
                }


                /* =====================================================
                   FORM ITEMS
                ===================================================== */

                .edit-product-form .ant-form-item {
                    margin-bottom: 10px;
                }

                .edit-product-form .ant-form-item-label {
                    padding-bottom: 3px;
                }

                .edit-product-form
                .ant-form-item-label
                > label {
                    color: #334155;
                    font-size: 12px;
                    font-weight: 600;
                }

                .edit-product-form
                .ant-form-item-explain {
                    min-height: 16px;
                    font-size: 11px;
                }


                /* =====================================================
                   FORM CONTROLS
                ===================================================== */

                .edit-product-form .ant-input {
                    height: 38px;
                    border-radius: 7px;
                }

                .edit-product-form .ant-input:hover,
                .edit-product-form .ant-input:focus {
                    border-color: #f97316;
                }

                .edit-product-form .ant-select {
                    width: 100%;
                }

                .edit-product-form
                .ant-select-selector {
                    min-height: 38px !important;
                    height: 38px !important;
                    border-radius: 7px !important;
                }

                .edit-product-form
                .ant-select-selection-item {
                    line-height: 36px !important;
                }

                .edit-product-form .ant-input-number {
                    width: 100%;
                    height: 38px;
                    border-radius: 7px;
                }

                .edit-product-form
                .ant-input-number-input {
                    height: 36px;
                }

                .edit-product-form
                .ant-input-number:hover,
                .edit-product-form
                .ant-input-number-focused {
                    border-color: #f97316;
                }


                /* =====================================================
                   UPDATE BUTTON
                ===================================================== */

                .edit-product-submit {
                    grid-column: 1 / -1;
                    margin-top: 2px;
                    padding-top: 3px;
                }

                .edit-product-submit .ant-btn {
                    width: 100%;
                    height: 42px;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 700;
                    box-shadow:
                        0 4px 10px rgba(22, 119, 255, 0.15);
                }


                /* =====================================================
                   TABLET
                ===================================================== */

                @media (max-width: 1200px) {

                    .product-list-container {
                        padding: 16px;
                    }

                    .product-header {
                        align-items: flex-start;
                    }

                    .product-header-actions {
                        width: 100%;
                        justify-content: flex-start;
                    }

                    .product-search-input {
                        width: 280px;
                    }

                    .product-desktop-table {
                        overflow-x: auto;
                    }

                    .product-desktop-table .ant-table {
                        min-width: 1250px;
                    }

                }


                /* =====================================================
                   MOBILE
                ===================================================== */

                @media (max-width: 768px) {

                    .product-list-container {
                        padding: 10px;
                    }

                    .product-desktop-table {
                        display: none;
                    }

                    .product-mobile-list {
                        display: block;
                    }

                    .product-header {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 12px;
                        margin-bottom: 15px;
                    }

                    .product-header-title {
                        width: 100%;
                        justify-content: center;
                        text-align: center;
                    }

                    .product-header h2 {
                        width: 100%;
                        font-size: 24px;
                        text-align: center;
                    }

                    .product-count {
                        width: 100%;
                        text-align: center;
                    }

                    .product-header-actions {
                        width: 100%;
                        display: grid;
                        grid-template-columns:
                            minmax(0, 1fr)
                            auto
                            auto;
                        gap: 7px;
                    }

                    .product-search-input {
                        width: 100%;
                    }

                    .product-header-actions
                    .ant-btn {
                        min-width: 40px;
                    }

                    .product-header-actions
                    .ant-btn.ant-btn-primary {
                        min-width: 90px;
                    }

                    .search-status {
                        margin-bottom: 10px;
                        font-size: 13px;
                    }

                    .pagination-container {
                        margin-top: 15px;
                        padding-bottom: 15px;
                        overflow-x: auto;
                        justify-content: center;
                    }


                    /* ================================================
                       MOBILE MODAL
                    ================================================ */

                    .product-edit-modal .ant-modal {
                        width: calc(100vw - 20px) !important;
                        max-width: calc(100vw - 20px) !important;
                        margin: 10px auto;
                    }

                    .product-edit-modal .ant-modal-header {
                        padding: 13px 15px !important;
                    }

                    .product-edit-modal .ant-modal-body {
                        max-height: calc(100vh - 95px);
                        padding: 12px 15px 14px !important;
                    }

                    .edit-product-image-row {
                        margin-bottom: 10px;
                        padding: 8px;
                    }

                    .edit-product-image-wrapper {
                        flex-basis: 72px;
                        width: 72px;
                        height: 72px;
                    }

                    .edit-product-image-actions-title {
                        font-size: 12px;
                    }

                    .edit-product-image-actions-text {
                        font-size: 10px;
                        margin-bottom: 6px;
                    }

                    .edit-product-image-actions .ant-btn {
                        height: 32px;
                    }

                }


                /* =====================================================
                   SMALL MOBILE
                ===================================================== */

                @media (max-width: 480px) {

                    .product-list-container {
                        padding: 8px;
                    }

                    .product-header h2 {
                        font-size: 22px;
                    }

                    .product-header-actions {
                        grid-template-columns:
                            minmax(0, 1fr)
                            auto;
                    }

                    .product-header-actions
                    .product-search-input {
                        grid-column: 1 / -1;
                    }

                    .product-header-actions
                    .ant-btn.ant-btn-primary {
                        min-width: 0;
                        grid-column: 1 / -1;
                    }

                    .mobile-product-card {
                        padding: 12px;
                    }

                    .mobile-product-grid {
                        gap: 6px;
                    }

                    .mobile-product-grid > div {
                        padding: 7px;
                    }

                    .mobile-product-grid span {
                        font-size: 9px;
                    }

                    .mobile-product-grid strong {
                        font-size: 11px;
                    }

                    .mobile-product-actions .ant-btn {
                        height: 36px;
                        font-size: 12px;
                    }


                    /* ================================================
                       SMALL EDIT MODAL
                    ================================================ */

                    .edit-product-modal-title {
                        font-size: 15px;
                    }

                    .product-edit-modal .ant-modal-header {
                        padding: 11px 12px !important;
                    }

                    .product-edit-modal .ant-modal-body {
                        max-height: calc(100vh - 80px);
                        padding: 9px 12px 12px !important;
                    }

                    .edit-product-image-row {
                        gap: 10px;
                        padding: 7px;
                    }

                    .edit-product-image-wrapper {
                        flex-basis: 65px;
                        width: 65px;
                        height: 65px;
                    }

                    .edit-product-image-placeholder {
                        font-size: 25px;
                    }

                    .edit-product-form
                    .ant-form-item-label
                    > label {
                        font-size: 11px;
                    }

                    .edit-product-form .ant-input {
                        height: 36px;
                    }

                    .edit-product-form
                    .ant-input-number {
                        height: 36px;
                    }

                    .edit-product-form
                    .ant-input-number-input {
                        height: 34px;
                    }

                    .edit-product-form
                    .ant-select-selector {
                        min-height: 36px !important;
                        height: 36px !important;
                    }

                    .edit-product-form
                    .ant-select-selection-item {
                        line-height: 34px !important;
                    }

                    .edit-product-form
                    .ant-form-item {
                        margin-bottom: 7px;
                    }

                    .edit-product-submit .ant-btn {
                        height: 39px;
                        font-size: 13px;
                    }

                }


                /* =====================================================
                   VERY SHORT SCREENS
                ===================================================== */

                @media (max-height: 700px) {

                    .product-edit-modal .ant-modal-body {
                        max-height: calc(100vh - 85px);
                        padding-top: 9px !important;
                        padding-bottom: 10px !important;
                    }

                    .edit-product-image-row {
                        margin-bottom: 7px;
                    }

                    .edit-product-image-wrapper {
                        width: 65px;
                        height: 65px;
                        flex-basis: 65px;
                    }

                    .edit-product-form
                    .ant-form-item {
                        margin-bottom: 6px;
                    }

                    .edit-product-form
                    .ant-form-item-label {
                        padding-bottom: 1px;
                    }

                    .edit-product-submit .ant-btn {
                        height: 37px;
                    }

                }


                /* =====================================================
                   REDUCED MOTION
                ===================================================== */

                @media (prefers-reduced-motion: reduce) {

                    .product-desktop-table
                    .ant-table-tbody > tr,
                    .product-desktop-table
                    .ant-table-tbody > tr > td,
                    .product-image-wrapper,
                    .product-image-placeholder,
                    .mobile-product-card {
                        transition: none !important;
                    }

                }

                `}
            </style>


            <div
                className="product-list-container"
            >

                {/* =================================================
                    HEADER
                ================================================= */}

                <div
                    className="product-header"
                >

                    <div
                        className="product-header-title"
                    >

                        <div>

                            <h2>
                                Product Inventory
                            </h2>

                            <p>
                                Manage stock, pricing and
                                profitability
                            </p>

                        </div>

                        <Tag
                            color="blue"
                            className="product-count"
                        >
                            {
                                totalProducts.toLocaleString()
                            }{" "}
                            Products
                        </Tag>

                    </div>


                    <div
                        className="product-header-actions"
                    >

                        <Input
                            className="product-search-input"
                            placeholder="Search products..."
                            prefix={
                                <SearchOutlined />
                            }
                            value={
                                searchText
                            }
                            onChange={e =>
                                handleSearch(
                                    e.target.value
                                )
                            }
                            allowClear
                            onClear={
                                handleClearSearch
                            }
                            size="large"
                        />


                        {searchText && (

                            <Button
                                size="large"
                                icon={
                                    <ClearOutlined />
                                }
                                onClick={
                                    handleClearSearch
                                }
                            >
                                Clear
                            </Button>

                        )}


                        <Tooltip
                            title="Refresh products"
                        >

                            <Button
                                size="large"
                                icon={
                                    <ReloadOutlined />
                                }
                                loading={
                                    refreshing
                                }
                                onClick={
                                    handleRefresh
                                }
                            />

                        </Tooltip>


                        <Button
                            type="primary"
                            size="large"
                            icon={
                                <FilePdfOutlined />
                            }
                            loading={
                                pdfLoading
                            }
                            onClick={
                                handleGeneratePDF
                            }
                        >
                            Stock PDF
                        </Button>

                    </div>

                </div>


                {/* =================================================
                    SEARCH STATUS
                ================================================= */}

                {searchText && (

                    <div
                        className="search-status"
                    >

                        <SearchOutlined />

                        <span>
                            Results for
                        </span>

                        <strong>
                            "{searchText}"
                        </strong>

                        <Tag>
                            {
                                totalProducts.toLocaleString()
                            }{" "}
                            results
                        </Tag>

                    </div>

                )}


                {/* =================================================
                    DESKTOP TABLE
                ================================================= */}

                <div
                    className="product-desktop-table"
                >

                    <Table
                        columns={
                            columns
                        }
                        dataSource={
                            products
                        }
                        rowKey="id"
                        pagination={false}
                        loading={{
                            spinning:
                                loading,

                            tip:
                                "Loading products...",
                        }}
                        locale={{
                            emptyText: (
                                <Empty
                                    image={
                                        <ShoppingCartOutlined
                                            style={{
                                                fontSize:
                                                    55,
                                            }}
                                        />
                                    }
                                    description={
                                        searchText
                                            ? "No products found"
                                            : "No products available"
                                    }
                                />
                            ),
                        }}
                        scroll={{
                            x:
                                1550,
                        }}
                        tableLayout="fixed"
                        size="middle"
                        sticky
                    />

                </div>


                {/* =================================================
                    MOBILE
                ================================================= */}

                <div
                    className="product-mobile-list"
                >

                    {loading &&
                    !products.length ? (

                        <div
                            className="mobile-loading"
                        >
                            Loading products...
                        </div>

                    ) : products.length ? (

                        products.map(
                            renderMobileProduct
                        )

                    ) : (

                        <Empty
                            image={
                                <ShoppingCartOutlined
                                    style={{
                                        fontSize:
                                            55,
                                    }}
                                />
                            }
                            description={
                                searchText
                                    ? "No products found"
                                    : "No products available"
                            }
                        />

                    )}

                </div>


                {/* =================================================
                    PAGINATION
                ================================================= */}

                {totalProducts > 0 && (

                    <div
                        className="pagination-container"
                    >

                        <Pagination
                            current={
                                currentPage
                            }
                            total={
                                totalProducts
                            }
                            pageSize={
                                PAGE_SIZE
                            }
                            showSizeChanger={
                                false
                            }
                            showQuickJumper
                            showTotal={
                                (
                                    total,
                                    range
                                ) => (
                                    <span>
                                        {
                                            range[0]
                                        }
                                        {" - "}
                                        {
                                            range[1]
                                        }
                                        {" of "}
                                        {
                                            total.toLocaleString()
                                        }
                                    </span>
                                )
                            }
                            onChange={
                                handlePageChange
                            }
                        />

                    </div>

                )}


                {/* =================================================
                    COMPACT EDIT MODAL
                ================================================= */}

                <Modal
                    open={
                        isModalOpen
                    }
                    centered
                    destroyOnHidden
                    footer={null}
                    className="product-edit-modal"
                    title={

                        <div
                            className="edit-product-modal-title"
                        >

                            <EditOutlined />

                            <span>
                                Edit Product
                            </span>

                        </div>

                    }
                    onCancel={
                        closeModal
                    }
                    width={540}
                >

                    <div
                        className="edit-product-modal"
                    >

                        <Form
                            form={
                                form
                            }
                            layout="vertical"
                            onFinish={
                                handleUpdate
                            }
                            className="edit-product-form"
                        >

                            {/* =========================================
                                IMAGE SECTION
                            ========================================= */}

                            <div
                                className="edit-product-image-row"
                            >

                                <div
                                    className="edit-product-image-wrapper"
                                >

                                    {previewImage ? (

                                        <img
                                            src={
                                                previewImage
                                            }
                                            alt="Product preview"
                                            className="edit-product-image"
                                        />

                                    ) : (

                                        <div
                                            className="edit-product-image-placeholder"
                                        >
                                            <ShoppingCartOutlined />
                                        </div>

                                    )}

                                </div>


                                <div
                                    className="edit-product-image-actions"
                                >

                                    <div
                                        className="edit-product-image-actions-title"
                                    >
                                        Product Image
                                    </div>

                                    <div
                                        className="edit-product-image-actions-text"
                                    >
                                        Upload a new image for
                                        this product.
                                    </div>

                                    <Upload
                                        beforeUpload={() =>
                                            false
                                        }
                                        maxCount={1}
                                        showUploadList={
                                            false
                                        }
                                        onChange={
                                            handleImageChange
                                        }
                                        accept="image/*"
                                    >

                                        <Button
                                            icon={
                                                <UploadOutlined />
                                            }
                                        >
                                            Change Image
                                        </Button>

                                    </Upload>

                                </div>

                            </div>


                            {/* =========================================
                                FORM GRID
                            ========================================= */}

                            <div
                                className="edit-form-grid"
                            >

                                {/* PRODUCT NAME */}

                                <Form.Item
                                    className="edit-form-full"
                                    label="Product Name"
                                    name="name"
                                    rules={[
                                        {
                                            required:
                                                true,

                                            whitespace:
                                                true,

                                            message:
                                                "Enter product name",
                                        },
                                    ]}
                                >

                                    <Input
                                        placeholder="Enter product name"
                                    />

                                </Form.Item>


                                {/* UNIT TYPE */}

                                <Form.Item
                                    label="Unit Type"
                                    name="unit_type"
                                    rules={[
                                        {
                                            required:
                                                true,

                                            message:
                                                "Select unit type",
                                        },
                                    ]}
                                >

                                    <Select
                                        options={[
                                            {
                                                value:
                                                    "single",

                                                label:
                                                    "Single Unit",
                                            },
                                            {
                                                value:
                                                    "carton",

                                                label:
                                                    "Carton",
                                            },
                                        ]}
                                    />

                                </Form.Item>


                                {/* QUANTITY */}

                                <Form.Item
                                    label={
                                        selectedUnitType ===
                                        "carton"
                                            ? "Carton Quantity"
                                            : "Quantity"
                                    }
                                    name="quantity"
                                    rules={[
                                        {
                                            required:
                                                true,

                                            message:
                                                "Enter quantity",
                                        },
                                    ]}
                                >

                                    <InputNumber
                                        min={0}
                                        precision={0}
                                        placeholder="0"
                                    />

                                </Form.Item>


                                {/* PIECES PER CARTON */}

                                {selectedUnitType ===
                                "carton" ? (

                                    <Form.Item
                                        label="Pieces Per Carton"
                                        name="units_per_carton"
                                        rules={[
                                            {
                                                required:
                                                    true,

                                                message:
                                                    "Enter pieces per carton",
                                            },
                                        ]}
                                    >

                                        <InputNumber
                                            min={1}
                                            precision={0}
                                            placeholder="0"
                                        />

                                    </Form.Item>

                                ) : (

                                    <Form.Item
                                        label="Pieces Per Carton"
                                    >

                                        <InputNumber
                                            value={1}
                                            disabled
                                        />

                                    </Form.Item>

                                )}


                                {/* SELLING PRICE */}

                                <Form.Item
                                    label="Selling Price / Piece"
                                    name="price"
                                    rules={[
                                        {
                                            required:
                                                true,

                                            message:
                                                "Enter selling price",
                                        },
                                    ]}
                                >

                                    <InputNumber
                                        min={0}
                                        precision={2}
                                        addonBefore="L$"
                                        placeholder="0.00"
                                    />

                                </Form.Item>


                                {/* PURCHASE COST */}

                                <Form.Item
                                    label="Purchase Cost / Carton"
                                    name="wholesale_price"
                                    rules={[
                                        {
                                            required:
                                                true,

                                            message:
                                                "Enter purchase cost",
                                        },
                                    ]}
                                >

                                    <InputNumber
                                        min={0}
                                        precision={2}
                                        addonBefore="L$"
                                        placeholder="0.00"
                                    />

                                </Form.Item>


                                {/* UPDATE */}

                                <div
                                    className="edit-product-submit"
                                >

                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={
                                            loading
                                        }
                                        icon={
                                            <EditOutlined />
                                        }
                                    >
                                        Update Product
                                    </Button>

                                </div>

                            </div>

                        </Form>

                    </div>

                </Modal>

            </div>

        </>
    );
};


export default ProductList;